import {
  type FavoriteRecipe,
  favoritesService,
} from "../services/favoritesService";
import type { Recipe, RecipeDetails } from "../services/recipeService";
import { useMutation, useQuery, useQueryClient } from "./useDataCache";
import { useUser } from "./useUser";

export type { FavoriteRecipe };

// Hook to load and manage household favorite recipes
// All household members share the same list
export function useFavorites() {
  const queryClient = useQueryClient();
  const { householdId, userId } = useUser();
  const queryKey = ["favorites", householdId];

  const { data: favorites = [], ...queryInfo } = useQuery({
    queryKey,
    queryFn: () => {
      if (!householdId) return Promise.resolve([]);
      return favoritesService.getFavorites(householdId);
    },
    enabled: !!householdId,
  });

  const addFavoriteMutation = useMutation({
    mutationFn: ({
      recipe,
      details,
    }: {
      recipe: Recipe;
      details: RecipeDetails;
    }) => {
      if (!householdId || !userId) throw new Error("Not authenticated");
      return favoritesService.addFavorite(recipe, details, householdId, userId);
    },
    onSuccess: (newFavorite) => {
      queryClient.setQueryData<FavoriteRecipe[]>(queryKey, (old) =>
        old ? [newFavorite, ...old] : [newFavorite],
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: (id: string) => favoritesService.removeFavorite(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<FavoriteRecipe[]>(queryKey);
      queryClient.setQueryData<FavoriteRecipe[]>(queryKey, (old) =>
        old ? old.filter((f) => f.id !== id) : old,
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const isFavorited = (recipeId: number) =>
    favorites.some((f) => f.recipe_id === recipeId);

  const getFavoriteId = (recipeId: number) =>
    favorites.find((f) => f.recipe_id === recipeId)?.id ?? null;

  return {
    favorites,
    ...queryInfo,
    addFavorite: addFavoriteMutation.mutateAsync,
    removeFavorite: removeFavoriteMutation.mutateAsync,
    isAdding: addFavoriteMutation.isPending,
    isFavorited,
    getFavoriteId,
  };
}
